import React, { useCallback, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import Select, { components } from 'react-select';
import { FIELD_POPOVER_Z_INDEX } from 'utils/constants';

// Types for props
interface OptionType {
  label: string;
  value: string;
}

interface AsyncMultiSelectAltProps {
  label: string;
  name: string;
  placeholder?: string;
  isClearable?: boolean;
  isSearchable?: boolean;
  className?: string;
  control: any;
  optionsPromise: () => Promise<OptionType[]>;
  error?: string;
  required?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

const CheckboxOption = (props: any) => {
  return (
    <components.Option {...props}>
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => {}}
        style={{ marginRight: 8 }}
      />
      <label>{props.label}</label>
    </components.Option>
  );
};

const MultiValueContainer = (props: any) => {
  // Render selected items as tags/chips
  return (
    <div className="flex flex-wrap gap-1">
      {props.getValue().map((option: OptionType) => (
        <div
          key={option.value}
          className="text-xs text-white bg-primary px-1.5 py-1 rounded-md mr-1 mb-1"
        >
          {option.label}
        </div>
      ))}
    </div>
  );
};

const AsyncMultiSelectAlt: React.FC<AsyncMultiSelectAltProps> = ({
  label,
  name,
  placeholder = 'Select option',
  isClearable = false,
  isSearchable = true,
  className = '',
  optionsPromise,
  control,
  error = '',
  required = false,
  loading = false,
  disabled = false,
}) => {
  const { field } = useController({ name, control });
  const [options, setOptions] = useState<OptionType[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const hasFetched = useRef(false);

  // Fetch options only when dropdown is opened for the first time
  const handleMenuOpen = useCallback(() => {
    setIsDropdownOpen(true);
    if (!hasFetched.current) {
      setIsOptionsLoading(true);
      optionsPromise()
        .then((opts) => {
          setOptions(opts);
          hasFetched.current = true;
        })
        .finally(() => setIsOptionsLoading(false));
    }
  }, [optionsPromise]);

  const handleMenuClose = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  // Map value (array of string) to option objects
  const selectedOptions = options.filter((opt) =>
    field.value?.includes(opt.value)
  );

  // Filter options by search
  const filteredOptions = search
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  return (
    <div className={`space-y-1 mb-2 ${className}`}>
      <div className="apart w-full">
        <div className="text-text-medium text-sm">
          {label}
          {required && <span className="text-error">*</span>}
        </div>
      </div>
      <Select
        isMulti
        isSearchable={isSearchable}
        isClearable={isClearable}
        isLoading={isOptionsLoading || loading}
        options={filteredOptions}
        value={selectedOptions}
        onChange={(selected: any) => {
          field.onChange(selected.map((s: OptionType) => s.value));
        }}
        placeholder={<span className="text-text-light">{placeholder}</span>}
        isDisabled={loading || disabled}
        closeMenuOnSelect={false}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        onInputChange={(inputValue) => setSearch(inputValue)}
        components={{
          Option: CheckboxOption,
          MultiValueContainer,
        }}
        styles={{
          menuPortal: (base: any) => ({
            ...base,
            zIndex: FIELD_POPOVER_Z_INDEX,
          }),
        }}
        menuPortalTarget={
          typeof window !== 'undefined' ? document.body : undefined
        }
      />
      {error && <div className="text-error text-xs">*{error}</div>}
    </div>
  );
};

export default AsyncMultiSelectAlt;
